// ---- FW-2A: Products & Services (read view of the sellable catalogue; items are edited on /items) ----
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { SalesCatalogueService } from '../../services/salesCatalogue.service';
import { SalesCatalogueItem } from '../../interfaces/sales/salesCatalogue.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney } from '../../utils/format';
import ScrollableTable from '../common/ScrollableTable';

const SalesCatalogue: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canManageItems = usePermission(PERMISSIONS.ITEM_MANAGE);
  const [rows, setRows] = useState<SalesCatalogueItem[]>([]);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handle = setTimeout(() => {
      setLoading(true);
      SalesCatalogueService.search(search, kind || undefined, 0, 100)
        .then(p => setRows(p.content ?? []))
        .catch(e => toast.showApiError(e, 'Failed to load the catalogue'))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, kind]);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.catalogue.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">Products &amp; Services</Typography>
        {canManageItems && (
          <Button variant="contained" startIcon={<AddIcon />} data-help="sales.catalogue.newService" onClick={() => navigate('/items')}>
            New service
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="sales.catalogue.filters">
        <TextField size="small" data-help="sales.catalogue.search" label="Search by name or code" value={search}
          onChange={e => setSearch(e.target.value)} sx={{ flex: '1 1 260px', maxWidth: 300 }} />
        <TextField select SelectProps={{ native: true }} size="small" data-help="sales.catalogue.kind" label="Kind" InputLabelProps={{ shrink: true }}
          value={kind} onChange={e => setKind(e.target.value)} sx={{ flex: '1 1 140px', maxWidth: 200 }}>
          <option value="">All kinds</option>
          <option value="STOCK">Stock items</option>
          <option value="SERVICE">Services</option>
          <option value="KIT">Kits</option>
        </TextField>
      </Box>
      <Paper data-help-section="sales.catalogue.table">
        {loading ? <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box> : (
          <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="sales.catalogue.col.code">Code</TableCell><TableCell data-help="sales.catalogue.col.name">Name</TableCell><TableCell data-help="sales.catalogue.col.kind">Kind</TableCell>
                  <TableCell data-help="sales.catalogue.col.unit">Unit</TableCell><TableCell align="right" data-help="sales.catalogue.col.salePrice">Sale price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={5} align="center">No sellable items. Mark an item Sellable on the Items page.</TableCell></TableRow>}
                {rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.code}</TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell><Chip size="small" label={r.kind === 'SERVICE' ? 'Service' : r.kind === 'KIT' ? 'Kit' : 'Stock'} /></TableCell>
                    <TableCell>{r.uom ?? '-'}</TableCell>
                    <TableCell align="right">{r.salePrice != null ? formatMoney(r.salePrice) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        )}
      </Paper>
    </Box>
  );
};

export default SalesCatalogue;
// ---- end FW-2A ----
